/**
 * Hook that wires the pure `reduceChatState` reducer into a React
 * component plus an effect interpreter. The reducer is fully tested
 * in `__tests__/state.test.ts`; this hook is the thin glue that turns
 * its `{state, effects}` results into:
 *
 *   - `useState`-backed state for re-render
 *   - synchronous effect dispatch through a caller-provided
 *     interpreter
 *
 * The interpreter is set via `setInterpreter` rather than passed at
 * construction so it can close over the latest `gameId` / IPC bridge
 * without re-creating the hook on every render. Effects produced
 * before the interpreter is set are queued and drained when it
 * arrives — this matters in the very first render, where the
 * `chat/loaded` action might fire `ipc/agent-send` for the
 * initial-prompt auto-send branch before the `useEffect` that wires
 * the interpreter has run.
 *
 * Effects fire synchronously inside `dispatch`. The IPCs they invoke
 * are async (they return Promises) so React's render cycle is never
 * blocked, but the dispatch contract is "by the time dispatch
 * returns, the effect has been handed to the interpreter." Tests on
 * the pure reducer don't go through this hook, so this is just for
 * production wiring.
 */

import { useCallback, useRef, useState } from 'react';
import {
  reduceChatState,
  type ChatAction,
  type ChatEffect,
  type ChatState,
} from './state.js';
import type { ChatMessage } from './types.js';

/**
 * Codex Round-3 YELLOW 2 on PR #463 (#461 follow-up).
 *
 * Read-only projection of `ChatState` returned by
 * `ChatReducerHandle.getState`. The underlying `ChatState.messages` is
 * typed as a mutable `ChatMessage[]` (the reducer mutates it in-place
 * for streaming text chunks — see `state.ts:155` and the chunk
 * handler), but callers of `getState()` MUST treat the result as
 * authoritative reducer state. A future caller doing
 * `getState().messages.push(...)` would silently mutate the reducer's
 * view without going through `dispatch`, desyncing the rendered state
 * from the source of truth.
 *
 * The narrowed return type projects `messages` to `ReadonlyArray<...>`
 * so a stray `.push` / `.splice` / `.sort` is a compile error at the
 * call site. The other array slices (`queuedMessages`,
 * `pendingAttachments`) are already declared `readonly T[]` on
 * `ChatState` itself, so the projection only needs to fix `messages`.
 *
 * Choice notes (per the YELLOW 2 ask). The alternative — a narrower
 * `getIsAiTyping(): boolean` accessor — would shrink the API to the
 * single field the current caller reads, but every future race-window
 * fix would need to add another accessor. Narrowing the projection
 * instead keeps the contract uniform: "the freshest reducer view, but
 * you may not mutate it." Future callers reading other fields get the
 * ref-fresh value for free, without API churn.
 */
export type ReadonlyChatState = Omit<ChatState, 'messages'> & {
  readonly messages: ReadonlyArray<ChatMessage>;
};

export interface ChatReducerHandle {
  state: ChatState;
  dispatch: (action: ChatAction) => void;
  /** Install the effect interpreter. Safe to call multiple times — the
   *  latest wins. Queued effects from before installation drain
   *  immediately on the first set. */
  setInterpreter: (fn: (effect: ChatEffect) => void) => void;
  /**
   * Read the latest authoritative state synchronously. Reads from the
   * same `stateRef` the reducer uses for `dispatch`, so this is the
   * post-dispatch source of truth even when React has not yet flushed
   * the `setState` triggered by a recent `dispatch` call.
   *
   * Codex Round-2 fold of WARN 6 on PR #463 (#461 follow-up). The
   * stale-state race window: between a `chunk/done` dispatch flipping
   * `isAiTyping` to false and React flushing the resulting render,
   * `stateRef.current.isAiTyping` reads `false` while the component's
   * rendered prop still reads the previous `true`. A speed-run click
   * landing in that window read the stale rendered prop, chose
   * `'dispatch'`, and emitted `user/type` + `user/send` — which
   * dispatched against the reducer's still-fresh `isAiTyping=true` view
   * (because dispatch reads `stateRef`, the source of truth for the
   * reducer) and queued `/build-game` for replay. Exposing the ref-fresh
   * getter lets the click handler read the same `isAiTyping` the
   * reducer will see, closing the race.
   *
   * Codex Round-3 YELLOW 2 fold: returns `ReadonlyChatState` (not
   * `ChatState`) so callers cannot mutate `messages` and silently
   * desync the reducer's view from the rendered tree. See the
   * `ReadonlyChatState` docblock above for the choice rationale.
   */
  getState: () => ReadonlyChatState;
}

export function useChatReducer(initial: ChatState): ChatReducerHandle {
  const [state, setState] = useState(initial);

  // Mirror state in a ref so dispatch can read the latest value
  // without going through React's setState callback (which has its
  // own quirks under StrictMode double-invocation).
  const stateRef = useRef(state);
  stateRef.current = state;

  const interpreterRef = useRef<((effect: ChatEffect) => void) | null>(null);
  const queuedEffectsRef = useRef<ChatEffect[]>([]);

  const dispatch = useCallback((action: ChatAction) => {
    const result = reduceChatState(stateRef.current, action);
    stateRef.current = result.state;
    setState(result.state);
    const interp = interpreterRef.current;
    if (interp) {
      for (const effect of result.effects) interp(effect);
    } else if (result.effects.length > 0) {
      queuedEffectsRef.current.push(...result.effects);
    }
  }, []);

  const setInterpreter = useCallback((fn: (effect: ChatEffect) => void) => {
    interpreterRef.current = fn;
    if (queuedEffectsRef.current.length > 0) {
      const queued = queuedEffectsRef.current;
      queuedEffectsRef.current = [];
      for (const effect of queued) fn(effect);
    }
  }, []);

  // Ref-fresh state getter (Codex Round-2 fold of WARN 6 on PR #463).
  // Stable callback identity (empty deps) so `useCallback` consumers
  // can include it in their dependency list without invalidating on
  // every render. The `stateRef` itself is mutated above on each render
  // before the hook returns, so the closure always sees the latest.
  const getState = useCallback(() => stateRef.current, []);

  return { state, dispatch, setInterpreter, getState };
}
